import { DateFormats, formatDate } from '@strata/helper-functions';
import { CurrentBid } from '@strata/react-current-bid';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import {
    ColumnDef,
    ColumnDefGeneric,
    GridWithFilters,
    ProductCell,
    SecondaryData,
} from '@strata/react-grid';
import { ComponentProps, useEffect } from 'react';

import {
    addQtyUnits,
    getEstDispensedQtyUnits,
    useProductConfig,
} from 'services/ProductConfigHelpers';
import { LostReferral, useGetLostReferralsQuery } from 'store/api/auctionApi';
import { useLoggedInSupplierName } from 'store/auth/authThunks';
import { Grids } from 'store/grids/gridsSlice';
import { useGridState } from 'store/grids/gridThunks';

const initialSortProperties: ComponentProps<
    typeof GridWithFilters<LostReferral>
>['sortProperties'] = [{ id: 'auctionEnd', desc: true }];

/* eslint-disable react/prop-types */
const QuantityCell: ColumnDefGeneric<LostReferral, 'qty'>['cell'] = ({ getValue, row }) => {
    const { segmentId, productServiceOid, benefit } = row.original;
    const productConfig = useProductConfig({ segmentId, productServiceOid, benefit });
    return <SecondaryData data={addQtyUnits(getValue(), getEstDispensedQtyUnits(productConfig))} />;
};
/* eslint-enable react/prop-types */

const columns: ColumnDef<LostReferral>[] = [
    {
        accessorKey: 'product',
        header: 'Product',
        size: 200,
        cell: ({ row, getValue }) => (
            <ProductCell product={getValue()} isBrand={row.original.isBrand} primary />
        ),
    },
    {
        accessorKey: 'strength',
        header: 'Strength',
        size: 80,
    },
    {
        accessorKey: 'qty',
        header: 'Est Qty',
        size: 80,
        cell: QuantityCell,
    },
    {
        accessorKey: 'bidAmt',
        header: 'Your Max Bid',
        size: 100,
        cell: ({ getValue, row }) => (
            <SecondaryData
                data={
                    <CurrentBid
                        bidAmount={getValue()}
                        referentPriceType={row.original.referentPriceType}
                    />
                }
            />
        ),
    },
    {
        accessorKey: 'auctionEnd',
        header: 'Servicing Start Date',
        size: 100,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
];

export const LostReferralsListPage = () => {
    const loggedInSupplierName = useLoggedInSupplierName();

    const { data, isLoading, refetch } = useGetLostReferralsQuery();
    useEffect(() => {
        (() => refetch())();
    }, [refetch]);

    const { sort, updateGridSort } = useGridState(Grids.LOST_REFERRALS, initialSortProperties);

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Lost Referrals"
                secondaryTitle={loggedInSupplierName}
                data={data}
                isLoading={isLoading}
                filterPaneRenderWidth={0}
                sortProperties={sort}
                onSort={updateGridSort}
                columns={columns}
            />
        </FormPageContainer>
    );
};
